import { featureOn } from "@lib/featureFlipper";
import sharedConstants from "@lib/shared_constants";

const { EMBEDDED_DEVICE, LIQUID, MULTI_LANGUAGE_COMPOSITION } = sharedConstants;

export const a11yLanguageLiquidTag = `{{${MULTI_LANGUAGE_COMPOSITION.A11Y_LANGUAGE_NAMESPACE}}}` as const;

export const LIQUID_REGEXES = {
  OBJECT_START: new RegExp(LIQUID.REGEXES.OBJECT_START, "g"),
  OBJECT_END: new RegExp(LIQUID.REGEXES.OBJECT_END, "g"),
  TAG_START: new RegExp(LIQUID.REGEXES.TAG_START, "g"),
  TAG_END: new RegExp(LIQUID.REGEXES.TAG_END, "g"),
  OBJECT: new RegExp(LIQUID.REGEXES.OBJECT, "g"),
  TAG: new RegExp(LIQUID.REGEXES.TAG, "g"),
  TAG_OR_OBJECT: new RegExp(LIQUID.REGEXES.TAG_OR_OBJECT, "g"),
};

export const liquidTypesToAnalyzeFor = [
  LIQUID.TYPES.EVENT_PROPERTIES,
  LIQUID.TYPES.CONTEXT_PROPERTIES,
  LIQUID.TYPES.API_TRIGGER_EVENT_PROPERTIES,
  LIQUID.TYPES.SMS_PROPERTIES,
  LIQUID.TYPES.WHATS_APP_PROPERTIES,
  LIQUID.TYPES.MOST_RECENT_DEVICE_ATTRIBUTES,
  LIQUID.TYPES.TARGETED_DEVICE_ATTRIBUTES,
  LIQUID.TYPES.CUSTOM_ATTRIBUTES,
];

const endRegex = new RegExp(`\\}(?=.*${LIQUID.REGEXES.TAG_END}|.*${LIQUID.REGEXES.OBJECT_END})`, "g");

const basicAttributesRegex = Object.keys(LIQUID.ALLOWED_ATTRIBUTES_WHITELIST)
  .filter((attr) => attr !== "most_recent_location")
  .join("|");
const allowedDeviceAttributesRegex = [
  ...Object.values(EMBEDDED_DEVICE.TEMPLATABLE_VALUES),
  ...(featureOn("new_ad_ids") ? Object.values(EMBEDDED_DEVICE.TEMPLATABLE_AD_ID_VALUES) : []),
].join("|");
const campaignAttributesRegex = Object.keys(LIQUID.ALLOWED_CAMPAIGN_ATTRIBUTES_WHITELIST).join("|");
const workflowAttributesRegex = [
  ...Object.keys(LIQUID.ALLOWED_WORKFLOW_ATTRIBUTES_WHITELIST),
  ...Object.keys(LIQUID.ALLOWED_WORKFLOW_VARIANT_ATTRIBUTES_WHITELIST),
].join("|");
const cardAttributesRegex = Object.keys(LIQUID.ALLOWED_CARD_ATTRIBUTES_WHITELIST).join("|");
export const emailAttributesRegex = Object.values(LIQUID.EMAIL)
  .filter((attr) => attr !== LIQUID.EMAIL.PREFERENCE_CENTER_URL_KEY)
  .join("|");
const smsAttributesRegex = Object.keys(LIQUID.ALLOWED_SMS_PROPERTIES_WHITELIST).join("|");

const whatsAppAttributesRegex = Object.keys(LIQUID.ALLOWED_WHATS_APP_PROPERTIES_WHITELIST)
  .filter(
    (attr) =>
      (attr !== "inbound_bsuid" || featureOn("whats_app_inbound_bsuid_attribute")) &&
      (attr !== "inbound_username" || featureOn("whats_app_inbound_username_attribute")) &&
      (attr !== "contact_shared_phone_number" || featureOn("whats_app_inbound_contact_share")) &&
      (attr !== "contact_share_origin" || featureOn("whats_app_inbound_contact_share"))
  )
  .join("|");

/**
 * @deprecated Use `useLiquidTemplateVariableRegexes` hook from `@services/useLiquidTemplateVariableRegexes` instead.
 * This constant does not react to feature flipper changes at runtime.
 */
export const TEMPLATE_VARIABLE_REGEXES = {
  [LIQUID.TYPES.BASIC_ATTRIBUTES]: new RegExp(`\\$\\{(${basicAttributesRegex})${endRegex.source}`, "g"),
  [LIQUID.TYPES.CUSTOM_ATTRIBUTES]: new RegExp(
    `${LIQUID.NAMESPACES.CUSTOM_ATTRIBUTES}\\.\\$\\{(.+?)${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.CONTENT_BLOCKS]: new RegExp(
    `${LIQUID.NAMESPACES.CONTENT_BLOCKS}\\.\\$\\{(.+?)${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.MOST_RECENT_DEVICE_ATTRIBUTES]: new RegExp(
    `${LIQUID.NAMESPACES.MOST_RECENT_DEVICE_ATTRIBUTES}\\.\\$\\{(${allowedDeviceAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.TARGETED_DEVICE_ATTRIBUTES]: new RegExp(
    `${LIQUID.NAMESPACES.TARGETED_DEVICE_ATTRIBUTES}\\.\\$\\{(${allowedDeviceAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.EVENT_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.EVENT_PROPERTIES}\\.\\$\\{(.+?)${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.CONTEXT_PROPERTIES]: new RegExp(
    `(?:${LIQUID.NAMESPACES.CONTEXT_PROPERTIES}|${LIQUID.NAMESPACES.CANVAS_ENTRY_PROPERTIES})\\.\\$\\{(.+?)${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.API_TRIGGER_EVENT_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.API_TRIGGER_EVENT_PROPERTIES}\\.\\$\\{(.+?)${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.CAMPAIGN_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.CAMPAIGN_PROPERTIES}\\.\\$\\{(${campaignAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.WORKFLOW_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.WORKFLOW_PROPERTIES}\\.\\$\\{(${workflowAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.CARD_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.CARD_PROPERTIES}\\.\\$\\{(${cardAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.EMAIL_PROPERTIES]: new RegExp(`\\$\\{(${emailAttributesRegex})${endRegex.source}`, "g"),
  [LIQUID.TYPES.SMS_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.SMS_PROPERTIES}\\.\\$\\{(${smsAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.WHATS_APP_PROPERTIES]: new RegExp(
    `${LIQUID.NAMESPACES.WHATS_APP_PROPERTIES}\\.\\$\\{(${whatsAppAttributesRegex})${endRegex.source}`,
    "g"
  ),
  [LIQUID.TYPES.MOST_RECENT_LOCATION]: new RegExp(
    `\\$\\{most_recent_location\\}\\.(latitude|longitude)(?=.*${LIQUID.REGEXES.TAG_END}|.*${LIQUID.REGEXES.OBJECT_END})`,
    "g"
  ),
};
